import { Div } from '@vgrubs/components';
import { ReactNode } from 'react';
import type {
  OutstandingBanner,
  VDriveBalanceLocation,
} from '../../types/api/outstandingBalance.types';
import { NoticeBanner } from './NoticeBanner';

type Props = {
  banners: OutstandingBanner[];
  /** Right of the message on desktop, under it on mobile - the merchant app hangs its Pay button here. */
  renderAction?: (banner: OutstandingBanner) => ReactNode;
  /**
   * What tapping the strip does, for a banner that has an action but no
   * button. A banner with a button is never clickable itself: only the
   * button acts.
   */
  bannerAction?: (banner: OutstandingBanner) => (() => void) | null;
  /**
   * Every location on the vDrive balance (`vDrive.balanceLocations`). With
   * more than one the balance is shared, and the vDrive banner says by whom.
   */
  vDriveBalanceLocations?: VDriveBalanceLocation[];
};

/**
 * "This vDrive billing is used across Uros REss, Bright Side, Brooklyn Diner,
 * Pizza" for a vDrive balance several locations share
 * (`vDrive.balanceLocations`), null for one of its own. Commas only: a
 * location's name can hold an "and".
 */
export const vDriveSharedByLabel = (
  balanceLocations: VDriveBalanceLocation[] | null | undefined
) =>
  balanceLocations && balanceLocations.length > 1
    ? `This vDrive billing is used across ${balanceLocations
        .map((location) => location.name)
        .join(', ')}`
    : null;

/**
 * The billing banners of one location, exactly as the backend wrote them.
 *
 * Every one is rendered, in the order sent (critical first). There is at most
 * one per service, and a vDrive banner never hides a SaaS one - both can be up
 * at once. The copy is written for staff too ("the owner can pay it in the
 * vGrubs app"), so it is not reworded here.
 *
 * Shared by the merchant app, where they are the notices, and the admin
 * receivables screen, where they show support what the merchant is looking at.
 *
 * Each drawn by NoticeBanner, like the tablet's banner: round "!", title and
 * message, the action on the right. Only the button acts; the strip is clickable only when it has
 * an action and no button. Title and message are shown exactly as sent: the backend
 * already writes the amount owed and any bank payment on its way into the
 * message, so nothing is added to them. Only a shared balance names its
 * locations under the message, as the backend asks.
 */
export const OutstandingBanners = ({
  banners,
  renderAction,
  bannerAction,
  vDriveBalanceLocations = [],
}: Props) => {
  const sharedBy = vDriveSharedByLabel(vDriveBalanceLocations);

  // The tablet drops a banner with nothing to say.
  const shown = banners.filter((banner) => banner.title || banner.message);
  if (shown.length === 0) return null;

  return (
    <Div $flex $column $gap="12px">
      {shown.map((banner) => {
        // Anything but critical is drawn as a warning, as on the tablet.
        const action = renderAction?.(banner);
        return (
          <NoticeBanner
            key={`${banner.service}-${banner.title}`}
            critical={banner.severity === 'critical'}
            title={banner.title}
            message={banner.message}
            note={banner.service === 'vDrive' ? sharedBy : null}
            action={action}
            onActivate={action ? null : bannerAction?.(banner) ?? null}
          />
        );
      })}
    </Div>
  );
};
